import React, { useEffect, useMemo, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Send, Loader2, CloudOff, RefreshCw, Trash2, Search, ShieldAlert, Star, Wand2, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { readQueue, enqueueResult, flushQueue, removeFromQueue, subscribeQueue, isNetworkError } from "@/lib/offlineQueue";
import { putPhoto, deletePhoto, photoObjectUrl } from "@/lib/photoStore";
import { isOnline, isSimulatedOffline, setSimulatedOffline, subscribeNet } from "@/lib/netSim";
import { TallyBar } from "@/components/TallyBar";
import { analyzePhoto } from "@/lib/photoQuality";

export default function AgentEntry() {
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [candidates, setCandidates] = useState([]);

  const [locs, setLocs] = useState({ state: [], lga: [], ward: [], polling_unit: [] });
  const [sel, setSel] = useState({ state: "", lga: "", ward: "", polling_unit: "" });
  const [votes, setVotes] = useState({});
  const [meta, setMeta] = useState({ total_registered: 0, total_accredited: 0, total_valid: 0, total_rejected: 0, notes: "" });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sheetPath, setSheetPath] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [photoQuality, setPhotoQuality] = useState(null);
  const [simOffline, setSimOffline] = useState(isSimulatedOffline());
  const [queue, setQueue] = useState(readQueue());
  const [syncing, setSyncing] = useState(false);
  const [offlineSheet, setOfflineSheet] = useState(null); // { key, previewUrl }
  const [puSearch, setPuSearch] = useState("");
  const [puSearchResults, setPuSearchResults] = useState([]);
  const [puSearchOpen, setPuSearchOpen] = useState(false);
  const [recentPUs, setRecentPUs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cw_recent_pus") || "[]").slice(0, 5); } catch { return []; }
  });
  const [selectedReported, setSelectedReported] = useState(false);

  const rememberPU = (pu) => {
    setRecentPUs(prev => {
      const next = [{ id: pu.id, name: pu.name, code: pu.code, path: pu.path, parent_id: pu.parent_id }, ...prev.filter(p => p.id !== pu.id)].slice(0, 5);
      try { localStorage.setItem("cw_recent_pus", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [assignments, setAssignments] = useState([]);
  const [stars, setStars] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrConfidence, setOcrConfidence] = useState({}); // candidate_id -> 0..1
  const [ocrPreview, setOcrPreview] = useState(null); // data URL of the exact page(s) sent to AI
  const [ocrMeta, setOcrMeta] = useState({ pages: 0, dropped: 0, minConf: 0 });
  const [starOnly, setStarOnly] = useState(false);
  useEffect(() => {
    api.get("/agents/me/assignments").then(({ data }) => setAssignments(data || [])).catch(() => setAssignments([]));
    api.get("/agents/me/stars").then(({ data }) => setStars(data || [])).catch(() => setStars([]));
  }, []);

  // Assignment Preload: when the agent has exactly one open admin assignment and nothing is picked yet, auto-select it.
  useEffect(() => {
    if (sel.polling_unit) return;
    const openAdmin = assignments.filter(a => a.source === "admin" && !a.reported);
    if (openAdmin.length === 1) {
      pickPUFromSearch(openAdmin[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments]);

  const toggleStar = async (pu) => {
    try {
      const { data } = await api.post("/agents/me/stars", { polling_unit_id: pu.id });
      const { data: s } = await api.get("/agents/me/stars");
      setStars(s || []);
      // reflect star immediately in assignments list
      setAssignments(prev => prev.map(a => a.id === pu.id ? { ...a, starred: data.starred } : a));
      toast.success(data.starred ? `⭐ Pinned ${pu.name}` : `Unpinned ${pu.name}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const runOcr = async () => {
    if (!sheetPath) return toast.error("Attach the sheet photo first");
    if (!electionId) return toast.error("Pick an election first");
    setOcrBusy(true);
    try {
      const { data } = await api.post("/results/ocr-hint", { election_id: electionId, sheet_path: sheetPath });
      const parsed = data?.votes || {};
      const conf = data?.confidence || {};
      let filled = 0;
      setVotes(v => {
        const next = { ...v };
        for (const [cid, val] of Object.entries(parsed)) {
          if (val !== null && val !== undefined) { next[cid] = val; filled++; }
        }
        return next;
      });
      setOcrConfidence(conf);
      setOcrPreview(data?.preview_data_url || null);
      setOcrMeta({ pages: data?.pages_rendered || 0, dropped: data?.below_threshold || 0, minConf: data?.min_confidence || 0 });
      const t = data?.turnout || {};
      setMeta(m => ({
        ...m,
        total_registered: t.registered ?? m.total_registered,
        total_accredited: t.accredited ?? m.total_accredited,
        total_valid: t.valid ?? m.total_valid,
        total_rejected: t.rejected ?? m.total_rejected,
      }));
      toast.success(`OCR filled ${filled} candidate${filled===1?"":"s"} — double-check the numbers before submit`);
      if (data?.below_threshold) {
        toast.info(`${data.below_threshold} vote${data.below_threshold===1?"":"s"} left blank (under the ${Math.round((data.min_confidence||0)*100)}% confidence threshold)`);
      }
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "OCR failed");
    } finally { setOcrBusy(false); }
  };

  // debounced polling-unit search — reported PUs are excluded server-side for the current election
  useEffect(() => {
    const q = puSearch.trim();
    if (q.length < 2) { setPuSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { q, level: "polling_unit", limit: 15, exclude_reported: true };
        if (electionId) params.election_id = electionId;
        const { data } = await api.get("/locations/search", { params });
        setPuSearchResults(data);
        setPuSearchOpen(true);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [puSearch, electionId]);

  const pickPUFromSearch = async (pu) => {
    if (pu?.reported) {
      toast.error(`${pu.name} is already reported — locked from further entry.`);
      return;
    }
    setPuSearchOpen(false); setPuSearch("");
    try {
      const { data: ward } = await api.get(`/locations/${pu.parent_id}`);
      const { data: lga } = await api.get(`/locations/${ward.parent_id}`);
      const { data: state } = await api.get(`/locations/${lga.parent_id}`);
      await loadChildren("lga", state.id);
      await loadChildren("ward", lga.id);
      await loadChildren("polling_unit", ward.id);
      setSel({ state: state.id, lga: lga.id, ward: ward.id, polling_unit: pu.id });
      setSelectedReported(false);
      rememberPU(pu);
      toast.success(`Selected ${pu.name}`);
    } catch { toast.error("Could not open that polling unit"); }
  };

  useEffect(() => {
    const unsubNet = subscribeNet((on) => { setOnline(on); setSimOffline(isSimulatedOffline()); if (on) void doSync("auto"); });
    const unsub = subscribeQueue(setQueue);
    // opportunistic sync every 30s when there is anything queued
    const t = setInterval(() => { if (isOnline() && readQueue().length) void doSync("interval"); }, 30000);
    return () => {
      unsubNet();
      unsub && unsub();
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSync = async (source = "manual") => {
    if (syncing || !isOnline()) { if (source === "manual" && isSimulatedOffline()) toast.info("Simulated offline — turn it off to sync"); return; }
    const q = readQueue();
    if (!q.length) { if (source === "manual") toast.info("Nothing queued to sync"); return; }
    setSyncing(true);
    try {
      const out = await flushQueue(api);
      if (out.rehearsed) toast.success(`Rehearsal complete — ${out.rehearsed} practice submission${out.rehearsed>1?"s":""} recorded (no real results created)`, { duration: 7000 });
      if (out.synced) toast.success(`${out.synced} result${out.synced>1?"s":""} synced${out.duplicate?` · ${out.duplicate} already on server`:""}`);
      if (out.failed) toast.warning(`${out.failed} still queued — will retry`);
      if (!out.synced && !out.failed && out.duplicate) toast.info(`${out.duplicate} already on server — queue cleaned`);
    } finally { setSyncing(false); }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/elections");
      setElections(data);
      if (data.length) setElectionId(data.find(e=>e.status==="active")?.id || data[0].id);
      const { data: st } = await api.get("/locations", { params: { level: "state", parent_id: "root" } });
      setLocs(l => ({...l, state: st}));
    })();
  }, []);

  useEffect(() => {
    if (!electionId) return;
    api.get(`/elections/${electionId}/candidates`).then(r => {
      setCandidates(r.data);
      setVotes(Object.fromEntries(r.data.map(c => [c.id, 0])));
    });
  }, [electionId]);

  const loadChildren = async (level, parent_id) => {
    const { data } = await api.get("/locations", { params: { level, parent_id: parent_id || "root" } });
    setLocs(l => ({...l, [level]: data}));
  };

  useEffect(() => { if (sel.state) loadChildren("lga", sel.state); else setLocs(l => ({...l, lga:[], ward:[], polling_unit:[]})); setSel(s => ({...s, lga:"", ward:"", polling_unit:""})); }, [sel.state]);
  useEffect(() => { if (sel.lga) loadChildren("ward", sel.lga); else setLocs(l => ({...l, ward:[], polling_unit:[]})); setSel(s => ({...s, ward:"", polling_unit:""})); }, [sel.lga]);
  useEffect(() => { if (sel.ward) loadChildren("polling_unit", sel.ward); else setLocs(l => ({...l, polling_unit:[]})); setSel(s => ({...s, polling_unit:""})); }, [sel.ward]);

  // Check reported status any time the selected polling unit or election changes.
  // If it's already reported, we lock the form and disable Submit so the agent can't overwrite it.
  useEffect(() => {
    if (!electionId || !sel.polling_unit) { setSelectedReported(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/elections/${electionId}/polling-units/${sel.polling_unit}/reported`);
        if (!cancelled) setSelectedReported(!!data?.reported);
      } catch { if (!cancelled) setSelectedReported(false); }
    })();
    return () => { cancelled = true; };
  }, [electionId, sel.polling_unit]);

  const totalVotes = useMemo(() => Object.values(votes).reduce((s,v)=> s + (parseInt(v)||0), 0), [votes]);
  const validEntered = parseInt(meta.total_valid) || 0;
  const mismatch = validEntered > 0 ? Math.abs(totalVotes - validEntered) : 0;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const q = await analyzePhoto(file);
    setPhotoQuality(q);
    if (q && !q.ok) toast.warning(`Photo looks ${q.issues.join(" and ")} — retake it for a readable sheet (you can still submit).`, { duration: 7000 });
    // If offline (or upload is likely to fail), stash the photo locally in IndexedDB
    if (!isOnline()) {
      const key = (window.crypto?.randomUUID?.() || `sheet-${Date.now()}`);
      try {
        await putPhoto(key, file);
        const url = URL.createObjectURL(file);
        setOfflineSheet({ key, previewUrl: url });
        setSheetPath(null);
        toast.info("Photo saved on this device — it will upload when back online.");
      } catch (err) { toast.error("Couldn't save the photo locally: " + err.message); }
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setSheetPath(data.path); setOfflineSheet(null);
      toast.success("Sheet uploaded");
    } catch (err) {
      if (isNetworkError(err) || !isOnline()) {
        const key = (window.crypto?.randomUUID?.() || `sheet-${Date.now()}`);
        try {
          await putPhoto(key, file);
          const url = URL.createObjectURL(file);
          setOfflineSheet({ key, previewUrl: url });
          setSheetPath(null);
          toast.warning("No connection — photo saved locally and will upload on sync.");
        } catch (e2) { toast.error("Local save failed: " + e2.message); }
      } else { toast.error(formatDetail(err.response?.data?.detail)); }
    }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!electionId || !sel.polling_unit) return toast.error("Select election and polling unit");
    setSubmitting(true);
    try {
      const payload = {
        election_id: electionId, polling_unit_id: sel.polling_unit,
        votes: Object.fromEntries(Object.entries(votes).map(([k,v]) => [k, parseInt(v)||0])),
        total_registered: parseInt(meta.total_registered)||0,
        total_accredited: parseInt(meta.total_accredited)||0,
        total_valid: parseInt(meta.total_valid)||totalVotes,
        total_rejected: parseInt(meta.total_rejected)||0,
        sheet_path: sheetPath, notes: meta.notes
      };
      // If a photo is only stored locally (offline attach), reference its IDB key
      // so the sync worker uploads it before submitting the result.
      if (!sheetPath && offlineSheet?.key) payload._sheet_key = offlineSheet.key;
      const puName = locs.polling_unit.find(p => p.id === sel.polling_unit)?.name || "polling unit";
      const electionName = elections.find(e => e.id === electionId)?.name || "election";
      try {
        // strip the client-only key from the online submission path
        const wire = { ...payload }; delete wire._sheet_key;
        if (!isOnline()) { const e = new Error("Network Error"); e.code = "ERR_NETWORK"; throw e; }
        await api.post("/results", wire);
        // if there was an offline photo but we never got to upload it, upload now
        if (!sheetPath && offlineSheet?.key) {
          try {
            const rec = await import("@/lib/photoStore").then(m => m.getPhoto(offlineSheet.key));
            if (rec) {
              const fd = new FormData(); fd.append("file", new File([rec.blob], rec.name || "sheet.jpg", { type: rec.type }));
              await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
            }
          } catch {}
          await deletePhoto(offlineSheet.key).catch(()=>{});
        }
        toast.success("Result submitted for approval");
      } catch (err) {
        if (err.response?.status === 423) {
          toast.error("This polling unit is already reported — locked from further entry.");
          setSelectedReported(true);
          return;
        }
        if (isNetworkError(err) || !isOnline()) {
          enqueueResult(payload, { pu_label: puName, election_label: electionName, vote_sum: totalVotes });
          toast.warning(`No connection — queued locally${payload._sheet_key ? " (photo included)" : ""}. Will auto-sync when you're back online.`);
        } else {
          throw err;
        }
      }
      setVotes(Object.fromEntries(candidates.map(c => [c.id, 0])));
      setMeta({ total_registered: 0, total_accredited: 0, total_valid: 0, total_rejected: 0, notes: "" });
      setSheetPath(null);
      if (offlineSheet?.previewUrl) URL.revokeObjectURL(offlineSheet.previewUrl);
      setOfflineSheet(null);
      setSel(s => ({...s, polling_unit: ""}));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Field Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="entry-title">Result Entry</h1>

      {assignments.filter(a => !a.reported).length > 0 && (
        <div className="mt-6 border border-emerald-500/20 bg-emerald-500/[0.03] p-4" data-testid="my-assignments">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-300">
              <Star size={12}/> Your Open Polling Units · {assignments.filter(a=>!a.reported && (!starOnly || a.starred)).length}{starOnly?` / ${assignments.filter(a=>!a.reported).length}`:""}
            </div>
            {assignments.filter(a => !a.reported).length > 5 && (
              <label className="flex items-center gap-2 cursor-pointer text-[10px] uppercase tracking-[0.2em] text-zinc-400 select-none" data-testid="star-only-toggle">
                <input type="checkbox" checked={starOnly} onChange={e=>setStarOnly(e.target.checked)} className="accent-amber-400 w-3.5 h-3.5"/>
                <Star size={11} className={starOnly?"text-amber-400 fill-amber-400":"text-zinc-500"}/> Starred only
              </label>
            )}
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            {assignments.filter(a => !a.reported && (!starOnly || a.starred)).sort((a,b)=> (b.starred?1:0)-(a.starred?1:0)).map(a => (
              <div key={a.id} className={`text-left p-3 border transition-colors relative ${a.starred?"border-amber-400/60 bg-amber-400/[0.06]":"border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/[0.05]"}`} data-testid={`assignment-${a.code}`}>
                <button type="button" onClick={()=>toggleStar(a)} data-testid={`star-${a.code}`}
                  className={`absolute top-2 right-2 p-1 ${a.starred?"text-amber-400":"text-zinc-600 hover:text-amber-300"}`} title={a.starred?"Unpin":"Pin as favourite"}>
                  <Star size={14} fill={a.starred?"currentColor":"none"}/>
                </button>
                <button type="button" onClick={()=>pickPUFromSearch(a)} className="text-left w-full pr-6">
                  <div className="text-emerald-400 tabular text-xs">{a.code}</div>
                  <div className="text-sm mt-1 truncate">{a.name}</div>
                  <div className="text-[10px] text-zinc-500 mt-1 truncate">{a.path}</div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1 flex items-center gap-2">
                    <span className="px-1 py-0.5 border border-white/10 tracking-widest">{a.source||""}</span>
                    {a.last_status && <>Last: <span className={a.last_status==="approved"?"text-emerald-400":a.last_status==="rejected"?"text-red-400":"text-amber-400"}>{a.last_status}</span></>}
                  </div>
                </button>
              </div>
            ))}
            {starOnly && assignments.filter(a => !a.reported && a.starred).length === 0 && (
              <div className="col-span-full text-xs text-zinc-500 text-center py-4">No starred polling units yet — tap the ★ icon on any card to pin it.</div>
            )}
          </div>
        </div>
      )}
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 border text-[11px] uppercase tracking-[0.2em] ${online ? "border-emerald-500/40 text-emerald-400" : "border-amber-400/50 text-amber-300 bg-amber-400/[0.06]"}`} data-testid="connectivity-badge">
          <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`}/>
          {online ? "Online" : simOffline ? "Offline · Rehearsal" : "Offline"}
          {queue.length > 0 && <span className="ml-2 text-white tabular">· {queue.length} queued</span>}
        </div>
        <label className={`flex items-center gap-2 px-3 py-1.5 border text-[11px] uppercase tracking-[0.15em] cursor-pointer select-none ${simOffline ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/15 text-zinc-400 hover:text-white"}`} title="Rehearse election day: pretend the network is down so submissions queue locally" data-testid="simulate-offline-toggle">
          <input type="checkbox" checked={simOffline} onChange={(e) => { setSimulatedOffline(e.target.checked); toast[e.target.checked ? "warning" : "success"](e.target.checked ? "Simulated offline ON — submissions will be saved locally" : "Back online — queued submissions will sync"); }} className="accent-amber-400 w-3.5 h-3.5" data-testid="simulate-offline-checkbox"/>
          Simulate offline
        </label>
      </div>

      {queue.length > 0 && (
        <div className="mt-6 border border-amber-400/40 bg-amber-400/[0.05] p-4" data-testid="offline-queue-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CloudOff size={18} className="text-amber-300"/>
              <div>
                <div className="text-sm font-semibold text-amber-200">{queue.length} submission{queue.length>1?"s":""} waiting to sync</div>
                <div className="text-[11px] text-amber-200/70 uppercase tracking-wider mt-0.5">
                  {online ? "Ready to sync — click below or it will retry automatically every 30s." : "Will auto-send when your connection comes back."}
                </div>
              </div>
            </div>
            <button onClick={()=>doSync("manual")} disabled={syncing || (!online && !simOffline)} data-testid="sync-now-btn"
              className="px-4 py-2 border border-amber-400/60 text-amber-200 hover:bg-amber-400/10 text-xs uppercase tracking-[0.2em] flex items-center gap-2 disabled:opacity-40">
              {syncing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
              {syncing ? "Syncing" : "Sync now"}
            </button>
          </div>
          <div className="mt-3 divide-y divide-amber-400/10 border-t border-amber-400/10">
            {queue.map((it) => (
              <div key={it.temp_id} className="py-2 flex items-center gap-3 text-xs" data-testid={`queue-item-${it.temp_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-amber-100 truncate">{it.meta?.pu_label || "Polling unit"} · <span className="text-amber-100/60">{it.meta?.election_label || ""}</span></div>
                  <div className="text-amber-200/50 mt-0.5">
                    queued {new Date(it.queued_at).toLocaleTimeString()} · {it.meta?.vote_sum ?? 0} votes
                    {it.retry_count > 0 && <span className="ml-2 text-red-300">· {it.retry_count} retr{it.retry_count>1?"ies":"y"}{it.last_error?` — ${it.last_error}`:""}</span>}
                  </div>
                </div>
                <button onClick={()=>{ if(confirm("Discard this queued submission? The data will be lost.")) removeFromQueue(it.temp_id); }}
                  className="p-1 text-amber-300/50 hover:text-red-400" data-testid={`queue-discard-${it.temp_id}`} title="Discard"><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 mt-8">
        {/* Left: sheet upload */}
        <div className="col-span-12 lg:col-span-5">
          <div className="border border-white/10 bg-[#111114] aspect-[3/4] relative overflow-hidden flex items-center justify-center">
            {sheetPath ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${sheetPath}?auth=${localStorage.getItem("ec_token")}`}
                alt="Result Sheet"
                className="w-full h-full object-contain"
                data-testid="sheet-preview"
              />
            ) : offlineSheet?.previewUrl ? (
              <>
                <img src={offlineSheet.previewUrl} alt="Offline Sheet" className="w-full h-full object-contain" data-testid="offline-sheet-preview"/>
                <div className="absolute top-3 left-3 bg-amber-400 text-black text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1">Offline · will upload</div>
              </>
            ) : (
              <div className="text-center px-8">
                <Upload size={32} strokeWidth={1.5} className="mx-auto text-zinc-500"/>
                <div className="mt-3 text-sm text-zinc-400">Upload Form EC8A</div>
                <div className="text-xs text-zinc-600 mt-1">Photo of the original polling-unit result sheet</div>
              </div>
            )}
          </div>
          {photoQuality && (
            <div className={`mt-3 px-3 py-2 border text-xs flex items-start gap-2 ${photoQuality.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-amber-400/50 bg-amber-400/10 text-amber-200"}`} data-testid="photo-quality" data-ok={photoQuality.ok}>
              <span className="font-semibold uppercase tracking-wider text-[10px] mt-0.5">{photoQuality.ok ? "Photo OK" : "Retake?"}</span>
              <span>{photoQuality.ok ? "Bright and sharp enough to read." : `Looks ${photoQuality.issues.join(" and ")}. Hold steady, add light, fill the frame with the sheet.`}</span>
            </div>
          )}
          <label className="mt-3 block cursor-pointer" data-testid="upload-sheet-label">
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="sheet-file-input"/>
            <div className="w-full py-3 border border-white/20 hover:bg-white/10 text-xs uppercase tracking-[0.3em] text-center flex items-center justify-center gap-2">
              {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading</> : <><Upload size={14}/> {sheetPath ? "Replace Sheet" : "Attach Sheet"}</>}
            </div>
          </label>
          <label className="mt-2 block cursor-pointer lg:hidden" data-testid="camera-sheet-label">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="sheet-camera-input"/>
            <div className="w-full py-4 bg-emerald-500 text-black hover:bg-emerald-400 text-xs uppercase tracking-[0.3em] font-semibold text-center flex items-center justify-center gap-2">
              {uploading ? <><Loader2 size={16} className="animate-spin"/> Uploading</> : <><Camera size={16}/> Take Photo of Sheet</>}
            </div>
          </label>
          {sheetPath && (
            <button type="button" onClick={runOcr} disabled={ocrBusy || !electionId} data-testid="ocr-hint-btn"
              className="mt-2 w-full py-3 border border-emerald-500/40 bg-emerald-500/[0.08] hover:bg-emerald-500/[0.16] text-emerald-200 text-xs uppercase tracking-[0.3em] flex items-center justify-center gap-2 disabled:opacity-40">
              {ocrBusy ? <><Loader2 size={14} className="animate-spin"/> Reading sheet…</> : <><Wand2 size={14}/> Auto-fill votes from photo</>}
            </button>
          )}
          {ocrPreview && (
            <div className="mt-3 border border-white/10 bg-black/40 p-2" data-testid="ocr-preview">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1.5 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-emerald-400"/> OCR saw this
                {ocrMeta.pages > 1 && <span className="text-zinc-500">· {ocrMeta.pages} pages merged</span>}
                {ocrMeta.minConf > 0 && <span className="text-amber-300">· threshold {Math.round(ocrMeta.minConf*100)}%</span>}
              </div>
              <img src={ocrPreview} alt="OCR preview" className="w-full max-h-64 object-contain bg-white"/>
            </div>
          )}
        </div>

        {/* Right: form */}
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <div className="border border-white/10 bg-[#111114] p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Election</div>
            <select value={electionId} onChange={(e)=>setElectionId(e.target.value)} className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm outline-none" data-testid="entry-election-select">
              {elections.map(e => <option key={e.id} value={e.id} className="bg-black">{e.name}</option>)}
            </select>

            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-3">Location</div>
            <div className="relative">
              <input value={puSearch} onChange={e=>setPuSearch(e.target.value)} onFocus={()=>setPuSearchOpen(true)} onBlur={()=>setTimeout(()=>setPuSearchOpen(false),150)}
                data-testid="pu-search" placeholder="🔎 Search 40k polling units by name or code (e.g. A010101, Meth JSS)…"
                className="w-full bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm mb-3"/>
              {recentPUs.length > 0 && !puSearch && (
                <div className="flex flex-wrap gap-1.5 -mt-2 mb-3" data-testid="recent-pus">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 self-center">Recent:</span>
                  {recentPUs.map(r => (
                    <button key={r.id} type="button" onClick={()=>pickPUFromSearch(r)} data-testid={`recent-pu-${r.code}`}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                      {r.code} · {r.name.slice(0, 24)}
                    </button>
                  ))}
                </div>
              )}
              {puSearchOpen && puSearchResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-[#0F0F12] border border-white/20 shadow-2xl" data-testid="pu-search-results">
                  {puSearchResults.map(r => (
                    <button key={r.id} type="button" onMouseDown={e=>{e.preventDefault(); pickPUFromSearch(r);}}
                      data-testid={`pu-hit-${r.code}`} className="w-full text-left px-3 py-2 hover:bg-white/10 border-b border-white/5">
                      <div className="text-sm text-white">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        <span className="text-emerald-400 tabular">{r.code}</span> · {r.path}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { level: "state", label: "Region", opts: locs.state },
                { level: "lga", label: "Municipal/District", opts: locs.lga },
                { level: "ward", label: "Constituency", opts: locs.ward },
                { level: "polling_unit", label: "Polling Unit", opts: locs.polling_unit },
              ].map(f => (
                // ACCRA
                <select key={f.level} value={sel[f.level]} onChange={(e)=>setSel(s=>({...s, [f.level]: e.target.value}))} disabled={!f.opts.length}
                  data-testid={`entry-${f.level}-select`}
                  className="bg-transparent border border-white/20 px-2 py-2 text-sm outline-none disabled:opacity-40">
                  <option value="" className="bg-black">— {f.label} —</option>
                  {f.opts.map(o => <option key={o.id} value={o.id} className="bg-black">{o.name}</option>)}
                </select>
              ))}
            </div>
          </div>

          <div className="border border-white/10 bg-[#111114]">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Candidate Votes</div>
              <div className="text-xs text-zinc-500 tabular">Sum: <span className="text-white font-semibold">{totalVotes.toLocaleString()}</span></div>
            </div>
            <div className="divide-y divide-white/5">
              {candidates.map(c => {
                const conf = ocrConfidence[c.id];
                const hasConf = typeof conf === "number" && conf > 0;
                const confTier = hasConf ? (conf >= 0.85 ? "high" : conf >= 0.6 ? "med" : "low") : null;
                const confColor = confTier === "high" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]"
                                 : confTier === "med" ? "border-amber-400/50 text-amber-300 bg-amber-400/[0.08]"
                                 : confTier === "low" ? "border-red-500/50 text-red-300 bg-red-500/[0.08]" : "";
                return (
                  <div key={c.id} className="px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4">
                    <div className="w-1.5 h-8" style={{ background: c.color }}/>
                    <div className="flex-1 min-w-[140px]">
                      <div className="text-sm truncate">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{c.party_code}</div>
                    </div>
                    {hasConf && (
                      <span data-testid={`ocr-confidence-${c.id}`}
                        className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 border ${confColor}`}
                        title={`OCR confidence for ${c.name}: ${Math.round(conf*100)}%`}>
                        AI · {Math.round(conf*100)}%
                      </span>
                    )}
                    <input type="number" min="0" inputMode="numeric" pattern="[0-9]*" enterKeyHint="next" value={votes[c.id] ?? 0}
                      onFocus={(e)=>e.target.select()}
                      onChange={(e)=>{ setVotes(v => ({...v, [c.id]: e.target.value})); if (ocrConfidence[c.id] !== undefined) setOcrConfidence(o => { const n = {...o}; delete n[c.id]; return n; }); }}
                      data-testid={`vote-input-${c.id}`}
                      className="w-full sm:w-32 min-h-[56px] sm:min-h-0 bg-transparent border border-white/20 px-3 py-2 text-right tabular text-2xl sm:text-lg outline-none focus:border-emerald-400"/>
                  </div>
                );
              })}
              {!candidates.length && <div className="p-6 text-zinc-500 text-sm">No candidates for this election.</div>}
            </div>
          </div>

          <div className="border border-white/10 bg-[#111114] p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Turnout</div>            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              {["total_registered", "total_accredited", "total_valid", "total_rejected"].map(k => (
                <label key={k} className="block">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace("total_","")}</div>
                  <input type="number" min="0" inputMode="numeric" pattern="[0-9]*" enterKeyHint="next" value={meta[k]} onFocus={(e)=>e.target.select()} onChange={(e)=>setMeta(m=>({...m,[k]:e.target.value}))}
                    data-testid={`meta-${k}`}
                    className="w-full min-h-[52px] sm:min-h-0 bg-transparent border-b border-white/20 py-1.5 text-2xl sm:text-lg tabular outline-none focus:border-emerald-400"/>
                </label>
              ))}
            </div>
            <textarea placeholder="Notes (optional)" value={meta.notes} onChange={(e)=>setMeta(m=>({...m,notes:e.target.value}))} rows={2} className="w-full mt-4 bg-transparent border border-white/20 p-3 text-sm outline-none resize-none"/>
            {mismatch > 0 && (
              <div className="mt-3 border border-amber-400/50 bg-amber-400/10 p-3 text-xs text-amber-200 flex items-start gap-2" data-testid="entry-mismatch">
                <span className="uppercase tracking-[0.2em] font-semibold text-amber-300">Reconciliation:</span>
                <span>Sum of candidate votes (<span className="tabular font-semibold">{totalVotes.toLocaleString()}</span>) differs from valid total (<span className="tabular font-semibold">{validEntered.toLocaleString()}</span>) by <span className="tabular font-semibold">{mismatch}</span>. Approvers will see this warning.</span>
              </div>
            )}
          </div>

          {selectedReported && (
            <div className="border border-red-500/50 bg-red-500/[0.08] p-4 flex items-start gap-3" data-testid="pu-locked-banner">
              <ShieldAlert className="text-red-400 shrink-0 mt-0.5" size={18}/>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-red-300">Polling unit locked</div>
                <div className="text-sm text-red-200/90 mt-1">A result has already been reported for this polling unit. Only an admin can supersede it after a rejection. Pick another polling unit to continue.</div>
              </div>
            </div>
          )}

          <div className="sticky bottom-0 lg:static -mx-4 sm:mx-0 px-4 sm:px-0 pb-[env(safe-area-inset-bottom)] bg-[#0A0A0A]/95 lg:bg-transparent backdrop-blur lg:backdrop-blur-0 pt-2 lg:pt-0 z-30 space-y-2">
          {candidates.length > 0 && <TallyBar totalVotes={totalVotes} validEntered={validEntered}/>}
          <button onClick={submit} disabled={submitting || !sel.polling_unit || !electionId || selectedReported} data-testid="submit-result-btn"
            className={`w-full py-4 min-h-[56px] font-semibold uppercase tracking-[0.2em] text-sm disabled:opacity-40 flex items-center justify-center gap-2 ${selectedReported ? "bg-zinc-800 text-zinc-500 cursor-not-allowed" : online ? "bg-emerald-500 text-black hover:bg-emerald-400" : "bg-amber-400 text-black hover:bg-amber-300"}`}>            {submitting ? <Loader2 size={14} className="animate-spin"/> : online ? <Send size={14}/> : <CloudOff size={14}/>}
            {selectedReported ? "Locked · Already Reported" : submitting ? "Submitting" : online ? "Submit for Approval" : "Save Offline (auto-sync later)"}
          </button>
          </div>
        </div>
      </div>
      {/* Floating Log Incident FAB */}
      <Link to="/app/incidents" data-testid="fab-log-incident"
        className="fixed bottom-24 lg:bottom-6 right-4 lg:right-6 z-40 bg-red-500 hover:bg-red-400 text-white px-4 py-3 shadow-[0_20px_50px_-10px_rgba(239,68,68,0.5)] flex items-center gap-2 transition-colors">
        <ShieldAlert size={18}/>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold">Log incident</span>
      </Link>
    </div>
  );
}
